import {
  PERCEPCOES,
  ROTULO_PERCEPCAO,
  TEXTO_ATENDIMENTO_MINIMO,
  TIPOS_DOCUMENTO,
  schemaCriarLead,
  type EntradaCriarLead,
  type ResultadoDuplicata,
} from '@baiocchi/shared'
import {
  Alerta,
  AreaTexto,
  Botao,
  CabecalhoCartao,
  Caixa,
  Campo,
  Cartao,
  ContadorTexto,
  CorpoCartao,
  Entrada,
  GrupoRadio,
  RodapeCartao,
  Selecao,
  useAvisos,
} from '@baiocchi/ui'
import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import { chaves } from '../../api/chaves'
import { clientesApi, leadsApi } from '../../api/leads'
import { useSessao } from '../../autenticacao/contexto-sessao'
import { IconeVoltar } from '../../componentes/icones'
import {
  dicaSeIndisponivel,
  opcoesDeCatalogo,
  opcoesDePessoas,
  opcoesDeReferencia,
  useAtendentesElegiveis,
  useCatalogoAtivo,
  useEquipes,
  useUnidades,
} from '../../ganchos/usar-catalogos'
import { useFormulario } from '../../ganchos/usar-formulario'
import { CabecalhoPagina } from '../../layout/layout-app'
import { apenasDigitos, localParaInstante } from '../../utilitarios/formatar'

interface EstadoFormulario {
  // Cliente
  nome: string
  email: string
  tipoDocumento: 'CPF' | 'CNPJ'
  documento: string
  ddiPrincipal: string
  telefonePrincipal: string
  ddiAlternativo: string
  telefoneAlternativo: string
  ddiWhatsapp: string
  telefoneWhatsapp: string
  // Endereço
  cep: string
  logradouro: string
  bairro: string
  complemento: string
  cidade: string
  estado: string
  // Profissional
  cargo: string
  empresa: string
  site: string
  // Atendimento
  unidadeId: string
  equipeId: string
  responsavelUuid: string
  percepcao: string
  pontoConversaoId: string
  produtoAssociadoId: string
  observacao: string
  // Agendamento opcional
  criarAgendamento: boolean
  agendamentoMotivoId: string
  agendamentoIniciaEm: string
  agendamentoObservacao: string
}

const INICIAL: EstadoFormulario = {
  nome: '',
  email: '',
  tipoDocumento: 'CPF',
  documento: '',
  ddiPrincipal: '55',
  telefonePrincipal: '',
  ddiAlternativo: '55',
  telefoneAlternativo: '',
  ddiWhatsapp: '55',
  telefoneWhatsapp: '',
  cep: '',
  logradouro: '',
  bairro: '',
  complemento: '',
  cidade: '',
  estado: '',
  cargo: '',
  empresa: '',
  site: '',
  unidadeId: '',
  equipeId: '',
  responsavelUuid: '',
  percepcao: 'NAO_IDENTIFICADO',
  pontoConversaoId: '',
  produtoAssociadoId: '',
  observacao: '',
  criarAgendamento: false,
  agendamentoMotivoId: '',
  agendamentoIniciaEm: '',
  agendamentoObservacao: '',
}

/**
 * TELA-03 — Novo lead.
 *
 * Quatro decisões que a tela herda do contrato, e não pode contrariar:
 *
 * 1. **`status` não é campo.** Quem decide o status inicial é o servidor
 *    (`04-leads-workflows.md §5`): sem agendamento nasce `EM_ABERTO`, com
 *    agendamento nasce `AGENDADO`. O corpo é `.strict()` — mandar `status` daria
 *    `422`.
 * 2. **Observação com no mínimo 20 caracteres**, contados após o `trim`, com
 *    contador ao vivo. É a mesma regra do `CHECK` do banco (RN-11).
 * 3. **Responsável depende de Equipe.** A lista vem de
 *    `GET /equipes/{id}/atendentes-elegiveis`, que aplica RN-14 no servidor. Antes
 *    de escolher a equipe, o campo fica desabilitado com o placeholder da spec.
 * 4. **Duplicata é checada ao sair do telefone** (RN-10). O aviso mostra o
 *    atendimento existente, mesmo que seja de outra equipe — a rota atravessa o
 *    escopo de propósito, porque avisar é a regra.
 */
export function PaginaNovoLead() {
  const navegar = useNavigate()
  const avisos = useAvisos()
  const clienteConsulta = useQueryClient()
  const { peloMenos } = useSessao()

  const [duplicata, setDuplicata] = useState<ResultadoDuplicata | null>(null)
  const [verificandoDuplicata, setVerificandoDuplicata] = useState(false)
  const [confirmarDuplicata, setConfirmarDuplicata] = useState(false)
  const [buscandoCep, setBuscandoCep] = useState(false)

  const formulario = useFormulario<EstadoFormulario>(INICIAL)
  const { valores, definir } = formulario

  const unidades = useUnidades()
  const equipes = useEquipes(valores.unidadeId || undefined)
  const atendentes = useAtendentesElegiveis(valores.equipeId ? Number(valores.equipeId) : null)
  const pontos = useCatalogoAtivo('pontos-conversao')
  const produtos = useCatalogoAtivo('produtos-associados')
  const motivosAgendamento = useCatalogoAtivo('motivos-agendamento')

  /**
   * Pré-checagem de duplicidade (RN-10). Roda no `blur` do telefone, não a cada
   * tecla: o telefone só é comparável quando está completo, e a rota tem custo.
   */
  async function verificarDuplicata() {
    const digitos = apenasDigitos(valores.telefonePrincipal)
    if (digitos.length < 10) return

    setVerificandoDuplicata(true)
    try {
      const resultado = await clientesApi.verificarDuplicata({
        telefone: `+${valores.ddiPrincipal}${digitos}`,
      })
      setDuplicata(resultado.encontrado ? resultado : null)
    } catch {
      // Falha na pré-checagem não bloqueia o cadastro: a deduplicação de verdade
      // é do servidor, no submit, e ela é que garante RN-10.
      setDuplicata(null)
    } finally {
      setVerificandoDuplicata(false)
    }
  }

  /** Busca de endereço por CEP — melhoria [NOVA] prevista na TELA-03. */
  async function buscarCep() {
    const digitos = apenasDigitos(valores.cep)
    if (digitos.length !== 8) return
    setBuscandoCep(true)
    try {
      const resposta = await fetch(`https://viacep.com.br/ws/${digitos}/json/`)
      const dados = (await resposta.json()) as {
        erro?: boolean
        logradouro?: string
        bairro?: string
        localidade?: string
        uf?: string
      }
      if (dados.erro) return
      formulario.definirVarios({
        logradouro: dados.logradouro ?? valores.logradouro,
        bairro: dados.bairro ?? valores.bairro,
        cidade: dados.localidade ?? valores.cidade,
        estado: dados.uf ?? valores.estado,
      })
    } catch {
      /* CEP é conveniência: falha de rede não impede o preenchimento manual. */
    } finally {
      setBuscandoCep(false)
    }
  }

  function montarCorpo(estado: EstadoFormulario): unknown {
    const telefone = (ddi: string, numero: string) =>
      numero.trim() ? { ddi, numero: apenasDigitos(numero) } : null

    return {
      cliente: {
        nome: estado.nome,
        email: estado.email.trim() || null,
        tipoDocumento: estado.documento.trim() ? estado.tipoDocumento : null,
        documento: estado.documento.trim() ? apenasDigitos(estado.documento) : null,
        contatoPrincipal: {
          ddi: estado.ddiPrincipal,
          numero: apenasDigitos(estado.telefonePrincipal),
        },
        contatoAlternativo: telefone(estado.ddiAlternativo, estado.telefoneAlternativo),
        whatsapp: telefone(estado.ddiWhatsapp, estado.telefoneWhatsapp),
        endereco: {
          cep: estado.cep.trim() || null,
          logradouro: estado.logradouro.trim() || null,
          bairro: estado.bairro.trim() || null,
          complemento: estado.complemento.trim() || null,
          cidade: estado.cidade.trim() || null,
          estado: estado.estado.trim() || null,
        },
        profissional: {
          cargo: estado.cargo.trim() || null,
          empresa: estado.empresa.trim() || null,
          site: estado.site.trim() || null,
        },
      },
      lead: {
        unidadeId: Number(estado.unidadeId),
        equipeId: Number(estado.equipeId),
        responsavelUuid: estado.responsavelUuid || null,
        percepcao: estado.percepcao,
        pontoConversaoId: estado.pontoConversaoId ? Number(estado.pontoConversaoId) : null,
        produtoAssociadoId: estado.produtoAssociadoId ? Number(estado.produtoAssociadoId) : null,
        tagIds: [],
      },
      observacao: estado.observacao,
      agendamento:
        estado.criarAgendamento && estado.agendamentoIniciaEm && estado.agendamentoMotivoId
          ? {
              motivoId: Number(estado.agendamentoMotivoId),
              iniciaEm: localParaInstante(estado.agendamentoIniciaEm),
              observacao: estado.agendamentoObservacao.trim() || null,
            }
          : null,
      confirmarDuplicata,
    }
  }

  async function submeter(evento: React.FormEvent) {
    evento.preventDefault()
    await formulario.enviar<EntradaCriarLead>(
      schemaCriarLead,
      montarCorpo,
      async (corpo) => {
        const resultado = await leadsApi.criar(corpo)
        void clienteConsulta.invalidateQueries({ queryKey: chaves.leads.todos })
        void clienteConsulta.invalidateQueries({ queryKey: chaves.painel.todos })
        void clienteConsulta.invalidateQueries({ queryKey: chaves.indicadores.todos })
        avisos.sucesso(
          'Lead cadastrado',
          resultado.clienteExistente
            ? 'Cliente já existia na base — este é um novo atendimento para ele.'
            : undefined,
        )
        await navegar(`/leads/${resultado.lead.uuid}`)
      },
    )
  }

  const exigeConfirmacaoDeGestor = duplicata?.leadEmAberto !== undefined && !peloMenos('GESTOR')

  return (
    <>
      <CabecalhoPagina
        titulo="Novo lead"
        descricao="Cadastro manual. A observação inicial é gravada como primeira entrada do histórico."
        acoes={
          <Botao
            variante="contorno"
            onClick={() => navegar(-1)}
            iconeEsquerda={<IconeVoltar className="h-4 w-4" />}
          >
            Voltar
          </Botao>
        }
      />

      <form onSubmit={submeter} noValidate className="flex flex-col gap-5">
        {formulario.erroGeral ? (
          <Alerta tom="perigo" titulo="Não foi possível cadastrar">
            {formulario.erroGeral}
          </Alerta>
        ) : null}

        {duplicata?.encontrado ? (
          <Alerta
            tom={duplicata.leadEmAberto ? 'atencao' : 'info'}
            titulo={
              duplicata.leadEmAberto
                ? 'Este cliente já tem atendimento em andamento'
                : 'Cliente já cadastrado'
            }
            acoes={
              duplicata.leadEmAberto ? (
                <>
                  <Botao
                    variante="contorno"
                    tamanho="sm"
                    onClick={() => navegar(`/leads/${duplicata.leadEmAberto?.uuid}`)}
                  >
                    Abrir atendimento existente
                  </Botao>
                  {!exigeConfirmacaoDeGestor && (
                    <Caixa
                      rotulo="Cadastrar mesmo assim"
                      checked={confirmarDuplicata}
                      onChange={(evento) => setConfirmarDuplicata(evento.target.checked)}
                    />
                  )}
                </>
              ) : undefined
            }
          >
            <p>
              <strong>{duplicata.cliente?.nome}</strong>
              {duplicata.leadEmAberto ? (
                <>
                  {' '}
                  está sendo atendido por{' '}
                  {duplicata.leadEmAberto.responsavel?.nome ?? 'ninguém (pool)'} na equipe{' '}
                  {duplicata.leadEmAberto.equipe?.titulo ?? '—'}.
                </>
              ) : (
                <> já existe na base. O cadastro criará um novo atendimento para o mesmo cliente.</>
              )}
            </p>
            {exigeConfirmacaoDeGestor ? (
              <p className="mt-2">
                Criar um segundo atendimento em andamento para o mesmo cliente exige confirmação de
                um gestor.
              </p>
            ) : null}
          </Alerta>
        ) : null}

        {/* --- Informações do lead --- */}
        <Cartao semPadding>
          <CabecalhoCartao
            titulo="Informações do lead"
            descricao="O contato principal é a chave de deduplicação do cliente."
          />
          <CorpoCartao className="grid gap-4 sm:grid-cols-2">
            <Campo rotulo="Nome" obrigatorio erro={formulario.erros['cliente.nome']} className="sm:col-span-2">
              {(atributos) => (
                <Entrada
                  {...atributos}
                  autoFocus
                  value={valores.nome}
                  temErro={atributos.temErro}
                  onChange={(evento) => definir('nome', evento.target.value)}
                />
              )}
            </Campo>

            <Campo rotulo="E-mail" erro={formulario.erros['cliente.email']}>
              {(atributos) => (
                <Entrada
                  {...atributos}
                  type="email"
                  inputMode="email"
                  value={valores.email}
                  temErro={atributos.temErro}
                  onChange={(evento) => definir('email', evento.target.value)}
                />
              )}
            </Campo>

            <div className="flex flex-col gap-1.5">
              <span className="text-sm font-medium text-conteudo">Tipo de documento</span>
              <GrupoRadio
                nome="tipoDocumento"
                valor={valores.tipoDocumento}
                onAlterar={(valor) => definir('tipoDocumento', valor)}
                opcoes={TIPOS_DOCUMENTO.map((tipo) => ({ valor: tipo, rotulo: tipo }))}
                className="h-10"
              />
            </div>

            <Campo
              rotulo={valores.tipoDocumento}
              erro={formulario.erros['cliente.documento']}
              dica="Opcional. Validado quando preenchido."
            >
              {(atributos) => (
                <Entrada
                  {...atributos}
                  inputMode="numeric"
                  value={valores.documento}
                  temErro={atributos.temErro}
                  onChange={(evento) => definir('documento', evento.target.value)}
                />
              )}
            </Campo>

            <CampoTelefone
              rotulo="Contato principal"
              obrigatorio
              ddi={valores.ddiPrincipal}
              numero={valores.telefonePrincipal}
              onAlterarDdi={(valor) => definir('ddiPrincipal', valor)}
              onAlterarNumero={(valor) => definir('telefonePrincipal', valor)}
              onSair={() => void verificarDuplicata()}
              erro={
                formulario.erros['cliente.contatoPrincipal.numero'] ??
                formulario.erros['cliente.contatoPrincipal']
              }
              dica={verificandoDuplicata ? 'Verificando duplicidade…' : undefined}
            />

            <CampoTelefone
              rotulo="Contato alternativo"
              ddi={valores.ddiAlternativo}
              numero={valores.telefoneAlternativo}
              onAlterarDdi={(valor) => definir('ddiAlternativo', valor)}
              onAlterarNumero={(valor) => definir('telefoneAlternativo', valor)}
            />

            <CampoTelefone
              rotulo="WhatsApp"
              ddi={valores.ddiWhatsapp}
              numero={valores.telefoneWhatsapp}
              onAlterarDdi={(valor) => definir('ddiWhatsapp', valor)}
              onAlterarNumero={(valor) => definir('telefoneWhatsapp', valor)}
              dica="Vazio herda o contato principal."
            />
          </CorpoCartao>
        </Cartao>

        {/* --- Informações do atendimento --- */}
        <Cartao semPadding>
          <CabecalhoCartao
            titulo="Informações do atendimento"
            descricao="Define de quem é o lead e por onde ele entrou."
          />
          <CorpoCartao className="grid gap-4 sm:grid-cols-2">
            <Campo
              rotulo="Unidade"
              obrigatorio
              erro={formulario.erros['lead.unidadeId']}
              dica={dicaSeIndisponivel(unidades, 'GET /api/v1/unidades')}
            >
              {(atributos) => (
                <Selecao
                  {...atributos}
                  vazio="Selecione a unidade"
                  value={valores.unidadeId}
                  temErro={atributos.temErro}
                  opcoes={opcoesDeReferencia(unidades.data?.dados)}
                  onChange={(evento) => {
                    definir('unidadeId', evento.target.value)
                    definir('equipeId', '')
                    definir('responsavelUuid', '')
                  }}
                />
              )}
            </Campo>

            <Campo
              rotulo="Equipe"
              obrigatorio
              erro={formulario.erros['lead.equipeId']}
              dica={
                !valores.unidadeId
                  ? 'Selecione uma unidade primeiro.'
                  : dicaSeIndisponivel(equipes, 'GET /api/v1/equipes')
              }
            >
              {(atributos) => (
                <Selecao
                  {...atributos}
                  vazio="Selecione a equipe"
                  value={valores.equipeId}
                  disabled={!valores.unidadeId}
                  temErro={atributos.temErro}
                  opcoes={opcoesDeReferencia(equipes.data?.dados)}
                  onChange={(evento) => {
                    definir('equipeId', evento.target.value)
                    definir('responsavelUuid', '')
                  }}
                />
              )}
            </Campo>

            <Campo
              rotulo="Responsável"
              erro={formulario.erros['lead.responsavelUuid']}
              dica={
                !valores.equipeId
                  ? 'Selecione uma equipe primeiro.'
                  : dicaSeIndisponivel(
                      atendentes,
                      'GET /api/v1/equipes/{id}/atendentes-elegiveis',
                      'Vazio deixa o lead na fila "sem responsável" da equipe.',
                    )
              }
            >
              {(atributos) => (
                <Selecao
                  {...atributos}
                  vazio="Sem responsável (pool da equipe)"
                  value={valores.responsavelUuid}
                  disabled={!valores.equipeId}
                  temErro={atributos.temErro}
                  opcoes={opcoesDePessoas(atendentes.data?.dados)}
                  onChange={(evento) => definir('responsavelUuid', evento.target.value)}
                />
              )}
            </Campo>

            <Campo rotulo="Percepção" obrigatorio erro={formulario.erros['lead.percepcao']}>
              {(atributos) => (
                <Selecao
                  {...atributos}
                  value={valores.percepcao}
                  temErro={atributos.temErro}
                  opcoes={PERCEPCOES.map((percepcao) => ({
                    valor: percepcao,
                    rotulo: ROTULO_PERCEPCAO[percepcao],
                  }))}
                  onChange={(evento) => definir('percepcao', evento.target.value)}
                />
              )}
            </Campo>

            <Campo
              rotulo="Ponto de conversão"
              erro={formulario.erros['lead.pontoConversaoId']}
              dica={dicaSeIndisponivel(
                pontos,
                'GET /api/v1/pontos-conversao',
                'É a dimensão principal dos relatórios de marketing — preencha sempre que souber.',
              )}
            >
              {(atributos) => (
                <Selecao
                  {...atributos}
                  vazio="Não informado"
                  value={valores.pontoConversaoId}
                  opcoes={opcoesDeCatalogo(pontos.data?.dados)}
                  onChange={(evento) => definir('pontoConversaoId', evento.target.value)}
                />
              )}
            </Campo>

            <Campo rotulo="Produto associado" erro={formulario.erros['lead.produtoAssociadoId']}>
              {(atributos) => (
                <Selecao
                  {...atributos}
                  vazio="Não informado"
                  value={valores.produtoAssociadoId}
                  opcoes={opcoesDeCatalogo(produtos.data?.dados)}
                  onChange={(evento) => definir('produtoAssociadoId', evento.target.value)}
                />
              )}
            </Campo>

            <Campo
              rotulo="Observações"
              obrigatorio
              className="sm:col-span-2"
              erro={formulario.erros.observacao}
              acessorio={
                <ContadorTexto valor={valores.observacao} minimo={TEXTO_ATENDIMENTO_MINIMO} />
              }
              dica="Descreve de onde o lead veio. Não conta como primeiro atendimento — o cliente ainda não foi acionado."
            >
              {(atributos) => (
                <AreaTexto
                  {...atributos}
                  rows={4}
                  value={valores.observacao}
                  temErro={atributos.temErro}
                  placeholder="Ex.: Cliente ligou pedindo informações sobre apartamento de 2 quartos na região central."
                  onChange={(evento) => definir('observacao', evento.target.value)}
                />
              )}
            </Campo>

            <div className="sm:col-span-2">
              <Caixa
                rotulo="Criar um agendamento junto com o lead"
                dica="Com agendamento, o lead nasce na situação Agendado."
                checked={valores.criarAgendamento}
                onChange={(evento) => definir('criarAgendamento', evento.target.checked)}
              />
            </div>

            {valores.criarAgendamento && (
              <>
                <Campo
                  rotulo="Data e hora"
                  obrigatorio
                  erro={formulario.erros['agendamento.iniciaEm']}
                  dica="Precisa ser no futuro."
                >
                  {(atributos) => (
                    <Entrada
                      {...atributos}
                      type="datetime-local"
                      value={valores.agendamentoIniciaEm}
                      temErro={atributos.temErro}
                      onChange={(evento) => definir('agendamentoIniciaEm', evento.target.value)}
                    />
                  )}
                </Campo>

                <Campo
                  rotulo="Motivo do agendamento"
                  obrigatorio
                  erro={formulario.erros['agendamento.motivoId']}
                >
                  {(atributos) => (
                    <Selecao
                      {...atributos}
                      vazio="Selecione o motivo"
                      value={valores.agendamentoMotivoId}
                      temErro={atributos.temErro}
                      opcoes={opcoesDeCatalogo(motivosAgendamento.data?.dados)}
                      onChange={(evento) => definir('agendamentoMotivoId', evento.target.value)}
                    />
                  )}
                </Campo>

                <Campo rotulo="Observação do agendamento" className="sm:col-span-2">
                  {(atributos) => (
                    <Entrada
                      {...atributos}
                      value={valores.agendamentoObservacao}
                      onChange={(evento) => definir('agendamentoObservacao', evento.target.value)}
                    />
                  )}
                </Campo>
              </>
            )}
          </CorpoCartao>
        </Cartao>

        {/* --- Endereço --- */}
        <Cartao semPadding>
          <CabecalhoCartao titulo="Endereço" descricao="Opcional. Preenchido pelo CEP quando possível." />
          <CorpoCartao className="grid gap-4 sm:grid-cols-3">
            <Campo
              rotulo="CEP"
              acessorio={
                <button
                  type="button"
                  onClick={() => void buscarCep()}
                  disabled={buscandoCep || apenasDigitos(valores.cep).length !== 8}
                  className="font-medium text-marca-600 hover:underline disabled:cursor-not-allowed disabled:text-conteudo-fraco disabled:no-underline"
                >
                  {buscandoCep ? 'Buscando…' : 'Buscar endereço'}
                </button>
              }
            >
              {(atributos) => (
                <Entrada
                  {...atributos}
                  inputMode="numeric"
                  value={valores.cep}
                  onBlur={() => void buscarCep()}
                  onChange={(evento) => definir('cep', evento.target.value)}
                />
              )}
            </Campo>

            <Campo rotulo="Logradouro" className="sm:col-span-2">
              {(atributos) => (
                <Entrada
                  {...atributos}
                  value={valores.logradouro}
                  onChange={(evento) => definir('logradouro', evento.target.value)}
                />
              )}
            </Campo>

            <Campo rotulo="Bairro">
              {(atributos) => (
                <Entrada
                  {...atributos}
                  value={valores.bairro}
                  onChange={(evento) => definir('bairro', evento.target.value)}
                />
              )}
            </Campo>

            <Campo rotulo="Complemento">
              {(atributos) => (
                <Entrada
                  {...atributos}
                  value={valores.complemento}
                  onChange={(evento) => definir('complemento', evento.target.value)}
                />
              )}
            </Campo>

            <div className="grid grid-cols-3 gap-3">
              <Campo rotulo="Cidade" className="col-span-2">
                {(atributos) => (
                  <Entrada
                    {...atributos}
                    value={valores.cidade}
                    onChange={(evento) => definir('cidade', evento.target.value)}
                  />
                )}
              </Campo>
              <Campo rotulo="UF" erro={formulario.erros['cliente.endereco.estado']}>
                {(atributos) => (
                  <Entrada
                    {...atributos}
                    maxLength={2}
                    value={valores.estado}
                    temErro={atributos.temErro}
                    onChange={(evento) => definir('estado', evento.target.value.toUpperCase())}
                  />
                )}
              </Campo>
            </div>
          </CorpoCartao>
        </Cartao>

        {/* --- Dados profissionais --- */}
        <Cartao semPadding>
          <CabecalhoCartao titulo="Dados profissionais" descricao="Opcional." />
          <CorpoCartao className="grid gap-4 sm:grid-cols-3">
            <Campo rotulo="Cargo">
              {(atributos) => (
                <Entrada
                  {...atributos}
                  value={valores.cargo}
                  onChange={(evento) => definir('cargo', evento.target.value)}
                />
              )}
            </Campo>
            <Campo rotulo="Empresa">
              {(atributos) => (
                <Entrada
                  {...atributos}
                  value={valores.empresa}
                  onChange={(evento) => definir('empresa', evento.target.value)}
                />
              )}
            </Campo>
            <Campo rotulo="Site">
              {(atributos) => (
                <Entrada
                  {...atributos}
                  value={valores.site}
                  onChange={(evento) => definir('site', evento.target.value)}
                />
              )}
            </Campo>
          </CorpoCartao>
          <RodapeCartao>
            <Link to="/leads" className="mr-auto text-sm text-conteudo-suave hover:underline">
              Cancelar
            </Link>
            <Botao type="submit" carregando={formulario.enviando}>
              Cadastrar lead
            </Botao>
          </RodapeCartao>
        </Cartao>
      </form>
    </>
  )
}

/** DDI + número, como nas referências. O DDI padrão é Brasil (+55). */
function CampoTelefone({
  rotulo,
  obrigatorio,
  ddi,
  numero,
  onAlterarDdi,
  onAlterarNumero,
  onSair,
  erro,
  dica,
}: {
  rotulo: string
  obrigatorio?: boolean
  ddi: string
  numero: string
  onAlterarDdi: (valor: string) => void
  onAlterarNumero: (valor: string) => void
  onSair?: () => void
  erro?: string
  dica?: string
}) {
  return (
    <Campo rotulo={rotulo} obrigatorio={obrigatorio} erro={erro} dica={dica}>
      {(atributos) => (
        <div className="flex gap-2">
          <Selecao
            value={ddi}
            onChange={(evento) => onAlterarDdi(evento.target.value)}
            className="w-28 shrink-0"
            aria-label={`DDI do ${rotulo.toLowerCase()}`}
            opcoes={[
              { valor: '55', rotulo: 'Brasil +55' },
              { valor: '1', rotulo: 'EUA +1' },
              { valor: '351', rotulo: 'Portugal +351' },
              { valor: '54', rotulo: 'Argentina +54' },
              { valor: '595', rotulo: 'Paraguai +595' },
            ]}
          />
          <Entrada
            {...atributos}
            inputMode="tel"
            placeholder="62 98460-7330"
            value={numero}
            temErro={atributos.temErro}
            onBlur={onSair}
            onChange={(evento) => onAlterarNumero(evento.target.value)}
          />
        </div>
      )}
    </Campo>
  )
}
